"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";

import { RichText, richTextHasContent, type RichTextValue } from "@/components/rich-text";
import { useBookingDrawer } from "@/lib/booking-context";
import type { Clinic } from "@/lib/clinics";

const ClinicMap = dynamic(() => import("@/components/bloks/clinic-map"), {
  ssr: false,
  loading: () => <div className="bg-dusk-01 size-full animate-pulse" />,
});

export default function GlobalClinicViewClient({
  clinic,
  about,
}: {
  clinic: Clinic;
  about: RichTextValue;
}) {
  const { setOpen } = useBookingDrawer();
  const aboutContent = richTextHasContent(about) ? about : clinic.summary;
  const hasMap = clinic.lat != null && clinic.lng != null;
  const address = [clinic.addressLine1, clinic.addressLine2]
    .filter(Boolean)
    .join(", ");
  const directionsUrl = hasMap
    ? `https://www.google.com/maps/dir/?api=1&destination=${clinic.lat},${clinic.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        [clinic.name, address].filter(Boolean).join(", "),
      )}`;

  return (
    <section className="bg-white">
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,5vw,4.5rem)]">
        {clinic.name && (
          <h2 className="text-purple-05 text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold">
            {clinic.name}
          </h2>
        )}

        {/* About text with a smaller image alongside. */}
        <div className="mt-6 grid gap-8 lg:mt-8 lg:grid-cols-[1.4fr_1fr] lg:items-start lg:gap-12">
          <RichText
            value={aboutContent}
            className="text-body flex flex-col gap-4 leading-[1.65] text-black/70"
          />
          {clinic.photo && (
            <div className="bg-dusk-01 relative order-first aspect-4/3 w-full overflow-hidden rounded-[20px] lg:order-0">
              <Image
                src={clinic.photo.filename}
                alt={clinic.photo.alt || clinic.name}
                fill
                sizes="(min-width: 1115px) 420px, 100vw"
                className="object-cover"
                loading="eager"
              />
            </div>
          )}
        </div>

        {/* Contact details beside the map. */}
        <div className="mt-10 grid gap-5 lg:mt-14 lg:grid-cols-[0.8fr_1.7fr] lg:gap-8">
          <div className="flex flex-col justify-between rounded-[20px] p-6 ring-1 ring-black/8">
            <div>
              <h3 className="text-h4 text-purple-05 font-bold">Contact</h3>
              <div className="mt-4 flex flex-col gap-4">
              {address && (
                <div className="flex gap-3">
                  <MapPin className="text-purple-03 mt-1 size-[18px] shrink-0" />
                  <p className="text-button leading-relaxed text-black/70">{address}</p>
                </div>
              )}
              {clinic.phone && (
                <div className="flex gap-3">
                  <Phone className="text-purple-03 mt-0.5 size-[18px] shrink-0" />
                  <a
                    href={`tel:${clinic.phone.replace(/\s+/g, "")}`}
                    className="text-button hover:text-purple-03 font-semibold text-black/70 transition-colors"
                  >
                    {clinic.phone}
                  </a>
                </div>
              )}
              {clinic.email && (
                <div className="flex gap-3">
                  <Mail className="text-purple-03 mt-0.5 size-[18px] shrink-0" />
                  <a
                    href={`mailto:${clinic.email}`}
                    className="text-button hover:text-purple-03 font-semibold break-all text-black/70 transition-colors"
                  >
                    {clinic.email}
                  </a>
                </div>
              )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setOpen(true, "clinic-view")}
              className="bg-purple-03 hover:bg-purple-04 mt-6 inline-flex h-12 w-full cursor-pointer items-center justify-center rounded-[8px] px-5 text-body font-bold text-white transition-colors md:h-14"
            >
              Book free consultation
            </button>
          </div>

          {hasMap && (
            <div className="relative">
              <div className="bg-dusk-01 isolate h-[320px] overflow-hidden rounded-[20px] lg:h-full lg:min-h-[360px]">
                <ClinicMap
                  pins={[{ clinic, num: 1 }]}
                  activeId={clinic.uuid}
                  hoveredId={null}
                  resetKey={0}
                  onSelect={() => {}}
                  markerVariant="logo"
                />
              </div>
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-button text-purple-05 hover:bg-dusk-01 absolute bottom-3 left-3 z-1000 inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-2 font-bold shadow-[0_6px_20px_-6px_rgba(39,6,88,0.28)] transition-colors"
              >
                Get directions
                <ArrowUpRight className="size-4" />
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
